import Image from 'next/image';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { Money } from '@/graphql/saleor/api';
import type { Maybe } from '@/lib/types';
import {
  commonMessages,
  OneyDownPaymentMessage,
  PaymentCost,
} from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { ReactComponent as InfoBadge } from '@/assets/icons/info-badge-grey.svg';
import OneyIcon from '@/assets/icons/oney.svg';

import * as S from '../ProductDetail.styles';

interface OneySentenceProps {
  onOneyClick: () => void;
  price: Maybe<Money>;
  variantCTA?: OrderAppCtaType;
}

export const OneySentence = ({
  onOneyClick,
  price,
  variantCTA,
}: OneySentenceProps) => {
  const { formatMessage } = useIntl();
  const { formatPrice } = useRegionProvider();
  const { oney3xPaymentMax, oney3xPaymentMin } = useConfigProvider();

  if (!price || variantCTA !== 'ORDER') return null;

  if (price.amount < oney3xPaymentMin || price.amount > oney3xPaymentMax)
    return null;

  return (
    <S.OneySentence data-testid='oneySentence' onClick={onOneyClick}>
      <Flex alignItems='center'>
        <Image alt='oney' height={16} src={OneyIcon} width={50} /> &nbsp;
        <Typography>{formatMessage(commonMessages.payIn)}</Typography>&nbsp;
        <S.Oney3xBadge>3x</S.Oney3xBadge>
      </Flex>
      <Typography>
        {OneyDownPaymentMessage(
          formatPrice(
            {
              amount: Math.ceil((price.amount / 3) * 100) / 100,
              currency: price.currency,
            },
            { currencyDisplay: 'symbol' },
          ),
        )}
      </Typography>
      <Typography>
        {PaymentCost(
          formatPrice(
            {
              amount: 0,
              currency: price.currency,
            },
            { currencyDisplay: 'symbol' },
          ),
        )}
        <InfoBadge height={12} width={12} />
      </Typography>
    </S.OneySentence>
  );
};
